import type { Client, Job } from '../../data/types';

export interface TermiteInspectionData {
  report: {
    jobId: string;
    inspectionTime: string;
    clientName: string;
    clientPhone: string;
    clientEmail: string;
    propertyAddress: string;
    billingAddress: string;
    scope: string;
    visualLimitationsConfirmed: boolean;
    noGuaranteeConfirmed: boolean;
    invasiveInspectionAuthorised: string;
  };
  property: {
    description: string;
    buildingType: string;
    height: string;
    construction: string;
    piers: string;
    floor: string;
    roof: string;
    fences: string;
    areasInspected: string;
  };
  access: {
    restrictedAreas: string;
    highRiskAreas: string;
    roofVoidInsulation: string;
    furnished: string;
    furnishingComments: string;
  };
  findings: {
    activeTermites: string;
    activityAreas: string;
    species: string;
    nestsFound: string;
    nestLocation: string;
    termiteWorkings: string;
    workingsAreas: string;
    damageFound: string;
    damageExtent: string;
    moistureReadings: string;
    moistureAreas: string;
    moistureComments: string;
  };
  management: {
    previousTreatment: string;
    durableSigns: string;
    treatmentRecommendation: string;
    retainingWallReplacement: string;
    termiteShields: string;
    physicalBarrier: string;
    chemicalZoneEvidence: string;
    previousCertificate: string;
    inspectionZoneVisibility: string;
    deficiencies: string;
    woodRot: string;
    woodRotAreas: string;
  };
  conditions: {
    riskFactors: string[];
    conduciveConditions: string;
    overallRisk: string;
    drainage: string;
    drainageActions: string;
    waterLeaks: string;
    leakActions: string;
    waterDischarge: string;
    ventilation: string;
    slabEdgeExposure: string;
    weepHoles: string;
    environmentalNotes: string;
  };
  recommendations: {
    managementProgram: string;
    treatmentProposalAttached: string;
    invasiveInspectionRecommended: string;
    expertReferral: string;
    urgentInstructionConfirmed: boolean;
    nextInspection: string;
    clientAdvice: string;
    additionalInformation: string;
  };
  certification: {
    inspectorName: string;
    inspectionDate: string;
    licence: string;
    confirmed: boolean;
  };
}

export const YES_NO = ['Not assessed', 'Yes', 'No'];
export const CONDITION = ['Not assessed', 'Adequate', 'Inadequate', 'Present', 'Not applicable'];
export const RISK_LEVELS = ['Not assessed', 'Low', 'Moderate', 'High', 'Very high', 'Extremely high'];
export const DAMAGE_SEVERITY = ['Not assessed', 'None', 'Minor', 'Moderate', 'Extensive', 'Severe', 'Unknown'];
export const MANAGEMENT_RECOMMENDATION = ['Not assessed', 'Essential', 'Recommended', 'Not required', 'Unknown'];
export const RISK_FACTORS = ['High moisture readings', 'Poor drainage', 'Water leaks', 'Water discharging against building', 'Inadequate ventilation', 'Slab edge concealed', 'Weep holes blocked or covered', 'Timber-to-ground contact', 'Landscaping timbers in soil contact', 'Timber debris, formwork or stumps', 'Stored firewood, timber or cardboard', 'Vegetation touching building', 'Termite-susceptible retaining walls', 'Subfloor clearance or access issue', 'Insulation or roof-void obstruction', 'Patios, paths or garden beds concealing inspection zones', 'Tree, post or fence termite risk', 'Previous treatment evidence unavailable or unclear'];

export function newTermiteInspection(job: Job, client: Client | null): TermiteInspectionData {
  return {
    report: { jobId: job.id, inspectionTime: job.startTime, clientName: client?.contact || client?.name || job.clientName, clientPhone: client?.phone || '', clientEmail: client?.email || '', propertyAddress: client?.address || '', billingAddress: '', scope: 'Visual termite inspection', visualLimitationsConfirmed: false, noGuaranteeConfirmed: false, invasiveInspectionAuthorised: 'No' },
    property: { description: client?.address || '', buildingType: '', height: '', construction: '', piers: '', floor: '', roof: '', fences: '', areasInspected: '' },
    access: { restrictedAreas: '', highRiskAreas: '', roofVoidInsulation: 'Not assessed', furnished: 'Not assessed', furnishingComments: '' },
    findings: { activeTermites: 'Not assessed', activityAreas: '', species: '', nestsFound: 'Not assessed', nestLocation: '', termiteWorkings: 'Not assessed', workingsAreas: '', damageFound: 'Not assessed', damageExtent: 'Not assessed', moistureReadings: 'Not assessed', moistureAreas: '', moistureComments: '' },
    management: { previousTreatment: '', durableSigns: '', treatmentRecommendation: '', retainingWallReplacement: 'Not assessed', termiteShields: 'Not assessed', physicalBarrier: 'Not assessed', chemicalZoneEvidence: '', previousCertificate: 'Not assessed', inspectionZoneVisibility: 'Not assessed', deficiencies: '', woodRot: 'Not assessed', woodRotAreas: '' },
    conditions: { riskFactors: [], conduciveConditions: '', overallRisk: 'Not assessed', drainage: 'Not assessed', drainageActions: '', waterLeaks: 'Not assessed', leakActions: '', waterDischarge: '', ventilation: 'Not assessed', slabEdgeExposure: 'Not assessed', weepHoles: 'Not assessed', environmentalNotes: '' },
    recommendations: { managementProgram: 'Not assessed', treatmentProposalAttached: 'Not assessed', invasiveInspectionRecommended: 'Not assessed', expertReferral: '', urgentInstructionConfirmed: false, nextInspection: '12 months', clientAdvice: '', additionalInformation: '' },
    certification: { inspectorName: job.techName, inspectionDate: job.date, licence: '', confirmed: false }
  };
}

/** Starts a revision from a saved inspection while adding any fields introduced in newer versions. */
export function reviseTermiteInspection(saved: Record<string, unknown>, job: Job, client: Client | null): TermiteInspectionData {
  const base = newTermiteInspection(job, client);
  const merged = { ...base } as Record<string, unknown>;
  for (const key of Object.keys(base) as Array<keyof TermiteInspectionData>) {
    if (saved[key] && typeof saved[key] === 'object') merged[key] = { ...(base[key] as object), ...(saved[key] as object) };
  }
  const result = merged as unknown as TermiteInspectionData;
  result.conditions.riskFactors ??= [];
  result.report = { ...result.report, visualLimitationsConfirmed: false, noGuaranteeConfirmed: false };
  result.certification = { ...result.certification, confirmed: false };
  return result;
}
